import { useCallback, useMemo, useState } from 'react'
import { useGetGrupoAnalises } from '../../../../../../../../../services/AgriculturaPrecisao/NewOSColeta/GET'
import { destroyAlert, errorAlert, loadingAlert } from '../../../../../../../../../utils/alert'
import {
  analisesSalvasParaCards,
  malhaSalvaParaPontos,
  parseShapefile,
} from '../components/analises/meta'
import type { AnaliseAdicionada, GeoPointFeature } from '../types'
import type { OsItensTypes } from '../../../../../../../../../services/OsParaColeta/GET'

interface UseAnalisesParams {
  /** Laboratório selecionado no formulário (dispara a busca do grupo de análises). */
  cod_laboratorio: number | null
  /** Data da OS, usada como data de vigência das análises do laboratório. */
  data_vigencia?: string
}

/** Converte "R$ 1.234,56" (ou número) em Number. */
function parseValorBRL(valor?: string): number {
  if (!valor) return 0
  const limpo = String(valor).replace(/[^\d,-]/g, '').replace(',', '.')
  const n = parseFloat(limpo)
  return isNaN(n) || !isFinite(n) ? 0 : n
}

/**
 * Estado e ações da metade "Análises" do formulário de OS.
 * Concentra malha/pontos/análises/totais e é exposto no FormOsContext sob `analises`.
 */
export function useAnalises({ cod_laboratorio, data_vigencia }: UseAnalisesParams) {
  //#region ESTADO
  const [malha, setMalha] = useState<GeoPointFeature[]>([])
  const [malhaShp, setMalhaShp] = useState<any>([])
  const [pontosTotalMalha, setPontosTotalMalha] = useState<number>(0)
  const [loadingShape, setLoadingShape] = useState<boolean>(false)
  const [talhaoKml, setTalhaoKml] = useState<any[]>([])
  const [talhaoArea, setTalhaoArea] = useState<string>('')
  const [analises, setAnalises] = useState<AnaliseAdicionada[]>([])

  /**
   * Assinatura das análises no estado "limpo" (vazio ao criar; a OS carregada
   * ao editar/copiar). Comparada com a assinatura atual para saber se o usuário
   * mexeu na metade "Análises" — ver `analisesDirty`.
   */
  const [assinaturaBase, setAssinaturaBase] = useState<string>('[]')

  // Seleção em curso (usada pelo modal de nova análise — sub-etapa futura)
  const [abrirModalNova, setAbrirModalNova] = useState<boolean>(false)
  const [id_em_edicao, setId_em_edicao] = useState<string | number | null>(null)
  const [cor, setCor] = useState<string>('')
  const [tipo_analise_selecionada, setTipo_analise_selecionada] = useState<string>('')
  const [tipo_analise_selecionada_id, setTipo_analise_selecionada_id] = useState<number | null>(null)
  const [pontos_selecionados, setPontos_selecionados] = useState<GeoPointFeature[]>([])
  const [pontos_selecionados_adicionados, setPontos_selecionados_adicionados] = useState<any[]>([])

  //#region GRUPO DE ANÁLISES (do laboratório)
  const { data_grupo_analises, carregando_grupo_analises } = useGetGrupoAnalises(
    cod_laboratorio,
    data_vigencia
  )

  /** Grupo com o rótulo composto "(profundidade) | análise" usado nos selects. */
  const grupoAnalisesTratado = useMemo(
    () =>
      (data_grupo_analises ?? []).map((item) => ({
        ...item,
        analise_profundidade_nome: `(${item?.profundidade_nome}) | ${item?.analise_nome}`,
      })),
    [data_grupo_analises]
  )

  //#region DIRTY
  /**
   * Resumo estável do conteúdo das análises (o que o usuário pode alterar:
   * quais análises existem, de que tipo e com quantos pontos). Evita comparar
   * os objetos inteiros, que carregam geometria e mudam de referência à toa.
   */
  const assinaturaAnalises = useCallback(
    (lista: AnaliseAdicionada[]) =>
      JSON.stringify(
        (lista ?? []).map((a: any) => [a?.id, a?.cod_analises?.cod, a?.pontos?.length ?? 0])
      ),
    []
  )

  const assinaturaAtual = useMemo(() => assinaturaAnalises(analises), [analises, assinaturaAnalises])

  /** O usuário adicionou/removeu/editou análises em relação à baseline? */
  const analisesDirty = assinaturaAtual !== assinaturaBase

  //#region AÇÕES
  const resetAnalises = useCallback(() => {
    setMalha([])
    setMalhaShp([])
    setPontosTotalMalha(0)
    setTalhaoKml([])
    setTalhaoArea('')
    setAnalises([])
    setAbrirModalNova(false)
    setId_em_edicao(null)
    setCor('')
    setTipo_analise_selecionada('')
    setTipo_analise_selecionada_id(null)
    setPontos_selecionados([])
    setPontos_selecionados_adicionados([])
    setAssinaturaBase('[]')
  }, [])

  /**
   * Pré-preenche a metade "Análises" a partir de uma OS carregada (editar/copiar).
   * Faz as transformações de malha/análises uma única vez e aplica em poucos setStates.
   */
  const hidratarAnalises = useCallback((os: OsItensTypes) => {
    if (!os) return
    setMalhaShp(os.malha_de_pontos ?? [])
    setMalha(malhaSalvaParaPontos(os.malha_de_pontos))
    setPontosTotalMalha(os.malha_de_pontos?.features?.length ?? 0)
    // Contorno vem como {latitude, longitude} (string); o mapa exige {lat, lng} numérico.
    const contorno = (os.talhao_kml?.coordenadas ?? [])
      .map((c: any) => ({ lat: Number(c?.latitude), lng: Number(c?.longitude) }))
      .filter((c) => !isNaN(c.lat) && !isNaN(c.lng))
    setTalhaoKml(contorno as any)
    // A área vem pronta na própria OS. Não dá para esperar apenas o talhão
    // selecionado resolver a área: a lista de talhões é outra requisição e ainda
    // filtra subdivisões (cod_talhao !== null), então a OS pode apontar para um
    // talhão fora da lista e a área ficaria em '0 ha'.
    if (os.talhao_area) setTalhaoArea(String(os.talhao_area))
    const analisesDaOs = analisesSalvasParaCards(os.analises as any)
    setAnalises(analisesDaOs)
    // A OS carregada é a baseline: hidratar não é alteração do usuário.
    setAssinaturaBase(assinaturaAnalises(analisesDaOs))
  }, [assinaturaAnalises])

  /** Importa um shapefile (.shp/.zip) e substitui a malha atual. */
  const importarMalha = useCallback(async (files: File[]) => {
    if (!files || files.length === 0) return
    try {
      setLoadingShape(true)
      loadingAlert({ msg: 'Processando malha...', id: 'import_malha' })
      const resultado = await parseShapefile(files)
      if (resultado) {
        setMalhaShp(resultado.malhaShp)
        setMalha(resultado.malha)
        setPontosTotalMalha(resultado.total)
      }
      destroyAlert('import_malha')
    } catch (error) {
      setMalhaShp([])
      errorAlert('Arquivo inválido. Verifique o shapefile e tente novamente.', 5000, 'import_malha')
    } finally {
      setLoadingShape(false)
    }
  }, [])

  const deletarAnalise = useCallback((analiseId: string | number) => {
    setAnalises((prev) => prev.filter((a) => a?.id !== analiseId))
  }, [])

  const removerPonto = useCallback((analiseId: string | number, pontoNome: number | string) => {
    setAnalises((prev) =>
      prev.map((analise) => {
        if (analise.id !== analiseId) return analise
        const novosPontos = analise.pontos_selecionados.filter((p) => p.NOME !== pontoNome)
        const valorUnitario = Number(analise.valor) || 0
        const novoValor = (valorUnitario * novosPontos.length).toLocaleString('pt-br', {
          style: 'currency',
          currency: 'BRL',
        })
        return { ...analise, pontos_selecionados: novosPontos, valor_analise: novoValor }
      })
    )
  }, [])

  /** Fecha o modal de nova análise e limpa a seleção em curso. */
  const fecharModalNova = useCallback(() => {
    setId_em_edicao(null)
    setCor('')
    setTipo_analise_selecionada('')
    setTipo_analise_selecionada_id(null)
    setPontos_selecionados([])
    setPontos_selecionados_adicionados([])
    setAbrirModalNova(false)
  }, [])

  /**
   * Persiste a seleção do modal de nova análise na lista de análises.
   * Se está editando (id_em_edicao), substitui o card; senão, adiciona.
   */
  const salvarPontosSelecionados = useCallback(() => {
    const dadosFormatados: AnaliseAdicionada[] = (pontos_selecionados_adicionados ?? []).map(
      (item: any) => ({
        id: item?.id || Date.now(),
        cod_analise: item?.cod_analise,
        pontos_selecionados: (item?.pontos_selecionados ?? []).map((p: GeoPointFeature) => ({
          ...p,
          COR: p.COR || item?.cor,
        })),
        profundidade: item?.profundidade,
        analise_nome: item?.analise_nome,
        valor: item?.valor,
        cor: item?.cor,
        valor_analise: item?.valor_analise,
      })
    )

    if (id_em_edicao) {
      setAnalises((prev) => prev.map((a) => (a.id === id_em_edicao ? dadosFormatados[0] : a)))
    } else {
      setAnalises((prev) => [...prev, ...dadosFormatados])
    }

    fecharModalNova()
  }, [pontos_selecionados_adicionados, id_em_edicao, fecharModalNova])

  /** Abre o modal de nova análise já com os pontos/análise atuais (edição de um card). */
  const inserirPontosSelecionados = useCallback(
    (pontos: GeoPointFeature[], id: string | number, cod_analise: number) => {
      const filtro = grupoAnalisesTratado.find((item) => item?.cod_analise === cod_analise)
      setTipo_analise_selecionada(filtro?.analise_profundidade_nome ?? '')
      setTipo_analise_selecionada_id(filtro?.cod_analise ?? null)
      setCor(filtro?.cor ?? '')
      setId_em_edicao(id)
      setPontos_selecionados(pontos)
      setAbrirModalNova(true)
    },
    [grupoAnalisesTratado]
  )

  //#region TOTAIS DERIVADOS
  const total_amostras = useMemo(
    () => analises.reduce((acc, item) => acc + (item.pontos_selecionados?.length || 0), 0),
    [analises]
  )

  const grid_amostral = useMemo(() => {
    if (pontosTotalMalha > 0) {
      return (parseFloat(talhaoArea) / pontosTotalMalha).toFixed(2)
    }
    return '0.00'
  }, [talhaoArea, pontosTotalMalha])

  const valor_total = useMemo(
    () => analises.reduce((acc, item) => acc + parseValorBRL(item.valor_analise), 0),
    [analises]
  )

  const rs_por_ha = useMemo(() => {
    const area = parseFloat(talhaoArea)
    if (!valor_total || !area || area <= 0 || !isFinite(area)) return 0
    return valor_total / area
  }, [valor_total, talhaoArea])

  return {
    // estado bruto
    malha,
    setMalha,
    malhaShp,
    setMalhaShp,
    pontosTotalMalha,
    setPontosTotalMalha,
    loadingShape,
    talhaoKml,
    setTalhaoKml,
    talhaoArea,
    setTalhaoArea,
    analises,
    setAnalises,

    // seleção em curso (para modal de nova análise)
    abrirModalNova,
    setAbrirModalNova,
    id_em_edicao,
    setId_em_edicao,
    cor,
    setCor,
    tipo_analise_selecionada,
    setTipo_analise_selecionada,
    tipo_analise_selecionada_id,
    setTipo_analise_selecionada_id,
    pontos_selecionados,
    setPontos_selecionados,
    pontos_selecionados_adicionados,
    setPontos_selecionados_adicionados,

    // grupo de análises
    grupoAnalises: data_grupo_analises,
    grupoAnalisesTratado,
    carregando_grupo_analises,

    // dirty
    analisesDirty,

    // ações
    resetAnalises,
    hidratarAnalises,
    importarMalha,
    deletarAnalise,
    removerPonto,
    inserirPontosSelecionados,
    salvarPontosSelecionados,
    fecharModalNova,

    // totais
    total_amostras,
    grid_amostral,
    valor_total,
    rs_por_ha,
  }
}
